import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  SafeAreaView,
  TextInput,
  Alert,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useTheme } from '../screens/ThemeContext';
import { auth, db } from '../private/firebaseConfig';
import { doc, getDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { updatePassword, signOut, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';

const StatusBar = Platform.OS === 'web' ? () => null : require('react-native').StatusBar;

export default function EditProfileScreenUser({ navigation }) {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    address: '',
    role: 'Utilisateur',
    status: '',
    isProfileComplete: false
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [errors, setErrors] = useState({});
  const [passwordErrors, setPasswordErrors] = useState({});

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        navigation.replace('LoginScreen');
        return;
      }

      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (!userDoc.exists()) {
        Alert.alert('Erreur', 'Profil utilisateur non trouvé');
        return;
      }

      const userData = userDoc.data();
      setFormData({
        fullName: userData.fullName || '',
        email: user.email || '',
        phone: userData.phone || '',
        address: userData.address || '',
        role: 'Utilisateur',
        status: userData.status || 'active',
        isProfileComplete: userData.isProfileComplete || false
      });
    } catch (error) {
      console.error('Error loading user data:', error);
      Alert.alert('Erreur', 'Impossible de charger les données du profil');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const phoneRegex = /^[0-9]{9}$/;

    if (!formData.fullName.trim()) newErrors.fullName = 'Le nom complet est requis';
    if (!formData.phone.trim()) {
      newErrors.phone = 'Le numéro de téléphone est requis';
    } else if (!phoneRegex.test(formData.phone.trim())) {
      newErrors.phone = 'Format de téléphone invalide (9 chiffres)';
    }
    if (!formData.address.trim()) newErrors.address = 'L\'adresse est requise';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validatePassword = () => {
    const newErrors = {};
    
    if (!passwordData.currentPassword) {
      newErrors.currentPassword = 'Mot de passe actuel requis';
    }
    
    if (!passwordData.newPassword) {
      newErrors.newPassword = 'Nouveau mot de passe requis';
    } else if (passwordData.newPassword.length < 6 || passwordData.newPassword.length > 8) {
      newErrors.newPassword = 'Le mot de passe doit contenir entre 6 et 8 caractères';
    }
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
    }

    setPasswordErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleUpdateProfile = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');

      const userRef = doc(db, 'users', user.uid);
      
      // Vérifier si tous les champs sont remplis
      const isComplete = Boolean(
        formData.fullName.trim() &&
        formData.phone.trim() &&
        formData.address.trim()
      );

      await updateDoc(userRef, {
        fullName: formData.fullName.trim(),
        phone: formData.phone.trim(),
        address: formData.address.trim(),
        updatedAt: new Date(),
        isProfileComplete: isComplete
      });

      if (isComplete) {
        Alert.alert('Succès', 'Votre profil a été mis à jour avec succès. Votre profil est complet !');
      } else {
        Alert.alert('Succès', 'Votre profil a été mis à jour avec succès');
      }
      
      setFormData(prev => ({
        ...prev,
        isProfileComplete: isComplete
      }));
    } catch (error) {
      console.error('Error updating profile:', error);
      Alert.alert('Erreur', 'Une erreur est survenue lors de la mise à jour du profil');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!validatePassword()) return;

    setSaving(true);
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');

      const credential = EmailAuthProvider.credential(user.email, passwordData.currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, passwordData.newPassword);

      Alert.alert('Succès', 'Votre mot de passe a été modifié avec succès');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      console.error('Error changing password:', error);
      Alert.alert('Erreur', 'Mot de passe actuel incorrect');
    } finally {
      setSaving(false);
      setShowPasswords({
        current: false,
        new: false,
        confirm: false,
      });
    }
  };

  const handleDeleteAccount = async () => {
    Alert.alert(
      'Supprimer le compte',
      'Êtes-vous sûr de vouloir supprimer votre compte ? Cette action est irréversible.',
      [
        { text: 'Annuler', style: 'cancel' },
        {
          text: 'Supprimer',
          style: 'destructive',
          onPress: async () => {
            try {
              const user = auth.currentUser;
              if (!user) return;
              await deleteDoc(doc(db, 'users', user.uid));
              await user.delete();
              await signOut(auth);
              navigation.replace('LoginScreen');
            } catch (error) {
              console.error('Error deleting account:', error);
              Alert.alert('Erreur', 'Impossible de supprimer le compte');
            }
          }
        }
      ]
    );
  };

  // Ajoute la fonction showAlert compatible web/mobile
  const showAlert = (title, message, buttons = null) => {
    if (Platform.OS === 'web') {
      if (buttons && buttons.length > 0) {
        const confirmed = window.confirm(`${title}\n\n${message}`);
        if (confirmed && buttons[1] && buttons[1].onPress) {
          buttons[1].onPress();
        }
      } else {
        window.alert(`${title}\n\n${message}`);
      }
    } else {
      if (buttons) {
        Alert.alert(title, message, buttons);
      } else {
        Alert.alert(title, message);
      }
    }
  };

  // Nouvelle fonction pour confirmation avant modification du profil
  const confirmUpdateProfile = () => {
    showAlert(
      'Confirmation',
      'Voulez-vous vraiment enregistrer les modifications de votre profil ?',
      [
        { text: 'Annuler', style: 'cancel' },
        {
          text: 'Enregistrer',
          style: 'default',
          onPress: handleUpdateProfile
        }
      ]
    );
  };

  // Nouvelle fonction pour confirmation avant modification du mot de passe
  const confirmChangePassword = () => {
    showAlert(
      'Confirmation',
      'Voulez-vous vraiment changer votre mot de passe ?',
      [
        { text: 'Annuler', style: 'cancel' },
        {
          text: 'Changer',
          style: 'default',
          onPress: handleChangePassword
        }
      ]
    );
  };

  const renderInput = (label, value, key, onChange, error, isPassword = false, passwordKey = null) => (
    <View style={styles.inputContainer}>
      <Text style={[styles.inputLabel, { color: theme.textSecondary }]}>{label}</Text>
      <View style={[styles.inputWrapper, { backgroundColor: theme.background, borderColor: theme.border }]}>
        <TextInput
          style={[styles.input, { color: theme.text }]}
          value={value}
          onChangeText={onChange}
          secureTextEntry={isPassword && !showPasswords[passwordKey]}
          editable={key !== 'email' && key !== 'role' && key !== 'status'}
          placeholderTextColor={theme.textSecondary}
        />
        {isPassword && (
          <TouchableOpacity
            onPress={() => setShowPasswords(prev => ({ ...prev, [passwordKey]: !prev[passwordKey] }))}
            style={styles.passwordIcon}
          >
            <MaterialCommunityIcons
              name={showPasswords[passwordKey] ? 'eye-off' : 'eye'}
              size={24}
              color={theme.textSecondary}
            />
          </TouchableOpacity>
        )}
      </View>
      {error && <Text style={styles.errorText}>{error}</Text>}
    </View>
  );

  if (loading) {
    return (
      <View style={[styles.loadingContainer, { backgroundColor: theme.background }]}>
        <ActivityIndicator size="large" color={theme.primary} />
      </View>
    );
  }

  return (
    <SafeAreaView style={[styles.safeArea, { backgroundColor: theme.primary }]}>
      <StatusBar barStyle={theme.statusBar} backgroundColor={theme.background} />
      
      <View style={[styles.header, { backgroundColor: theme.background }]}>
        <TouchableOpacity 
          onPress={() => navigation.goBack()}
          style={[styles.backButton, { backgroundColor: theme.surface, borderColor: theme.border }]}
        >
          <MaterialCommunityIcons name="arrow-left" size={24} color={theme.primary} />
        </TouchableOpacity>
        <Text style={[styles.headerTitle, { color: theme.text }]}>
          Modifier le profil
        </Text>
      </View>

      <ScrollView style={[styles.container, { backgroundColor: theme.background }]}>
        <View style={styles.formContainer}>
          {formData.isProfileComplete && (
            <View style={[styles.completeStatus, { backgroundColor: theme.success + '20' }]}>
              <MaterialCommunityIcons name="check-circle" size={24} color={theme.success} />
              <Text style={[styles.completeStatusText, { color: theme.success }]}>
                Votre profil est complet
              </Text>
            </View>
          )}

          <View style={[styles.section, { backgroundColor: theme.surface, borderColor: theme.border }]}>
            <Text style={[styles.sectionTitle, { color: theme.text }]}>
              Informations personnelles
            </Text>
            {renderInput(
              'Nom complet',
              formData.fullName,
              'fullName',
              (text) => setFormData(prev => ({ ...prev, fullName: text })),
              errors.fullName
            )}
            {renderInput(
              'Adresse e-mail',
              formData.email,
              'email',
              null,
              errors.email
            )}
            {renderInput(
              'Téléphone',
              formData.phone,
              'phone',
              (text) => setFormData(prev => ({ ...prev, phone: text })),
              errors.phone
            )}
            {renderInput(
              'Adresse',
              formData.address,
              'address',
              (text) => setFormData(prev => ({ ...prev, address: text })),
              errors.address
            )}
            {renderInput(
              'Type de compte',
              formData.role,
              'role',
              null
            )}
            {formData.status && (
              <View style={styles.statusContainer}>
                <Text style={[styles.statusLabel, { color: theme.textSecondary }]}>
                  Statut du compte:
                </Text>
                <View style={[
                  styles.statusBadge,
                  { backgroundColor: 
                    formData.status === 'active' ? theme.success + '20' :
                    formData.status === 'pending' ? theme.warning + '20' :
                    theme.danger + '20'
                  }
                ]}>
                  <Text style={[
                    styles.statusText,
                    { color: 
                      formData.status === 'active' ? theme.success :
                      formData.status === 'pending' ? theme.warning :
                      theme.danger
                    }
                  ]}>
                    {formData.status === 'active' ? 'Actif' :
                     formData.status === 'pending' ? 'En attente' :
                     'Suspendu'}
                  </Text>
                </View>
              </View>
            )}
          </View>

          <View style={[styles.section, { backgroundColor: theme.surface, borderColor: theme.border }]}>
            <Text style={[styles.sectionTitle, { color: theme.text }]}>
              Changer le mot de passe
            </Text>
            {renderInput(
              'Mot de passe actuel',
              passwordData.currentPassword,
              'currentPassword',
              (text) => setPasswordData(prev => ({ ...prev, currentPassword: text })),
              passwordErrors.currentPassword,
              true,
              'current'
            )}
            {renderInput(
              'Nouveau mot de passe',
              passwordData.newPassword,
              'newPassword',
              (text) => setPasswordData(prev => ({ ...prev, newPassword: text })),
              passwordErrors.newPassword,
              true,
              'new'
            )}
            {renderInput(
              'Confirmer le mot de passe',
              passwordData.confirmPassword,
              'confirmPassword',
              (text) => setPasswordData(prev => ({ ...prev, confirmPassword: text })),
              passwordErrors.confirmPassword,
              true,
              'confirm'
            )}
            <TouchableOpacity 
              style={[styles.changePasswordButton, { backgroundColor: theme.primary }]}
              onPress={confirmChangePassword}
              disabled={saving}
            >
              {saving ? (
                <ActivityIndicator color="#fff" />
              ) : (
                <>
                  <MaterialCommunityIcons name="key" size={20} color="#fff" />
                  <Text style={styles.changePasswordButtonText}>
                    Changer le mot de passe
                  </Text>
                </>
              )}
            </TouchableOpacity>
          </View>

          <TouchableOpacity 
            style={[styles.deleteAccountButton, { borderColor: '#ff4444' }]}
            onPress={handleDeleteAccount}
          >
            <MaterialCommunityIcons name="delete" size={20} color="#ff4444" />
            <Text style={styles.deleteAccountText}>Supprimer mon compte</Text>
          </TouchableOpacity>
        </View>

        <TouchableOpacity 
          style={[styles.saveButton, { backgroundColor: theme.primary }]}
          onPress={confirmUpdateProfile}
          disabled={saving}
        >
          {saving ? (
            <ActivityIndicator color="#fff" />
          ) : (
            <Text style={styles.saveButtonText}>Enregistrer les modifications</Text>
          )}
        </TouchableOpacity>
        
        <View style={styles.bottomSpace} />
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  safeArea: {
    flex: 1,
  },
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
  },
  backButton: {
    padding: 8,
    borderRadius: 12,
    marginRight: 15,
    borderWidth: 1,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  formContainer: {
    padding: 20,
  },
  completeStatus: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 12,
    marginBottom: 20,
  },
  completeStatusText: {
    marginLeft: 8,
    fontSize: 14,
    fontWeight: '600',
  },
  section: {
    borderRadius: 15,
    padding: 15,
    marginBottom: 20,
    borderWidth: 1,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 15,
  },
  inputContainer: {
    marginBottom: 15,
  },
  inputLabel: {
    fontSize: 14,
    marginBottom: 8,
  },
  inputWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderRadius: 12,
    overflow: 'hidden',
  },
  input: {
    flex: 1,
    height: 48,
    paddingHorizontal: 15,
    fontSize: 16,
  },
  passwordIcon: {
    padding: 10,
  },
  errorText: {
    color: '#ff4444',
    fontSize: 12,
    marginTop: 5,
    marginLeft: 5,
  },
  statusContainer: {
    marginTop: 15,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  statusLabel: {
    fontSize: 14,
  },
  statusBadge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
  },
  statusText: {
    fontSize: 12,
    fontWeight: '600',
  },
  changePasswordButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 15,
    borderRadius: 12,
    marginTop: 15,
  },
  changePasswordButtonText: {
    color: '#fff',
    marginLeft: 10,
    fontSize: 16,
    fontWeight: 'bold',
  },
  deleteAccountButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 15,
    borderRadius: 15,
    borderWidth: 1,
    marginTop: 10,
  },
  deleteAccountText: {
    color: '#ff4444',
    marginLeft: 10,
    fontSize: 14,
    fontWeight: '600',
  },
  saveButton: {
    margin: 20,
    padding: 15,
    borderRadius: 12,
    alignItems: 'center',
  },
  saveButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
  },
  bottomSpace: {
    height: 40,
  }
});